import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthForm } from '../../hooks/useAuthForm';
import PhoneInput from 'react-phone-input-2';
import Tippy from '@tippy.js/react';
import { Eye, EyeOff } from 'lucide-react';
import 'react-phone-input-2/lib/style.css';
import 'tippy.js/dist/tippy.css';

interface AuthFormProps {
  mode: 'signin' | 'signup';
}

export function AuthForm({ mode }: AuthFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { loading, error, signIn, signUp } = useAuthForm();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (mode === 'signup') {
      if (password !== confirmPassword) {
        return;
      }
      if (password.length < 6) {
        return;
      }
      await signUp({ email, password, phone, displayName });
    } else {
      await signIn({ email, password });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-500/10 text-red-400 p-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {mode === 'signup' && (
        <div>
          <label htmlFor="displayName" className="block text-sm font-medium text-gray-300">
            Name
          </label>
          <input
            id="displayName"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="mt-1 block w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-indigo-500 focus:ring-indigo-500"
            required
          />
        </div>
      )}

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-300">
          Email
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 block w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-indigo-500 focus:ring-indigo-500"
          required
        />
      </div>

      {mode === 'signup' && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="phone" className="block text-sm font-medium text-gray-300">
              Phone number
            </label>
            <Tippy 
              content="A phone number will allow us to better support you in the configuration and installation of your chatbot."
              placement="top"
              duration={0}
              className="!bg-gray-900 !text-white !border !border-white/10 !px-3 !py-2 !rounded-lg !text-sm !shadow-xl"
            >
              <span className="text-sm text-gray-400 italic cursor-help">
                recommended
              </span>
            </Tippy>
          </div>
          <div className="[&_.special-label]:!bg-transparent [&_.selected-flag]:!bg-white/5 [&_.selected-flag]:!border-white/10 [&_.country-list]:!bg-gray-900 [&_.country-list]:!text-white [&_.country-list_li.highlight]:!bg-white/10 [&_.country-list_li:hover]:!bg-white/5">
            <PhoneInput
              country={'fr'}
              value={phone}
              onChange={setPhone}
              inputClass="!w-full !bg-white/5 !border-white/10 !text-white !py-2 !px-3 !rounded-md focus:!border-indigo-500 !pl-12"
              containerClass="!bg-transparent"
              buttonClass="!bg-white/5 !border-white/10 !border-r-0"
              dropdownClass="!bg-gray-900 !text-white !border-white/10"
              searchClass="!bg-gray-800 !text-white !border-white/10 !mb-2"
            />
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="password" className="block text-sm font-medium text-gray-300">
            Password
          </label>
          {mode === 'signin' && (
            <Link to="/forgot-password" className="text-indigo-400 hover:text-indigo-300 text-sm">
              Forgot password?
            </Link>
          )}
        </div>
        <div className="relative">
          <input
            id="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 block w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-indigo-500 focus:ring-indigo-500 pr-10"
            required
            minLength={6}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        {mode === 'signup' && password.length > 0 && password.length < 6 && (
          <p className="mt-1 text-sm text-red-400">
            Password must be at least 6 characters long
          </p>
        )}
      </div>

      {mode === 'signup' && (
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
            Confirm password
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 block w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-indigo-500 focus:ring-indigo-500 pr-10"
              required
              minLength={6}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors"
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {password !== confirmPassword && confirmPassword !== '' && (
            <p className="mt-1 text-sm text-red-400">
              Passwords do not match
            </p>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={loading || (mode === 'signup' && (password !== confirmPassword || password.length < 6))}
        className="w-full bg-gradient-to-r from-violet-600 to-cyan-500 text-white px-4 py-2 rounded-xl hover:shadow-[0_0_30px_rgba(79,70,229,0.3)] transition-all disabled:opacity-50"
      >
        {loading ? 'Loading...' : mode === 'signin' ? 'Sign in' : 'Create account'}
      </button>
    </form>
  );
}